import { useSearchParams } from 'react-router-dom';
import {
  ModelSortDirection,
  SearchTasksSortBy,
  TaskSearchStatus,
  TaskSearchType,
} from '@admiin-com/ds-graphql';
import { useCurrentUser } from '../../../hooks/useCurrentUser/useCurrentUser';
import { useTaskTogglePreference } from '../useTaskTogglePreference/useTaskTogglePreference';
import { useEffect } from 'react';

export type TaskSearchFilters = {
  search: string | null;
  status?: TaskSearchStatus | 'ALL' | null; // Add 'ALL' as a valid type
  sentBy: string;
  myTasks: boolean;
  type: TaskSearchType;
  dueAtStart?: string | null;
  dueAtEnd?: string | null;
  createdBy?: string;
  limit?: number;
  sortBy?: SearchTasksSortBy;
  sortDirection?: ModelSortDirection;
};

export default function useTaskSearchFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useCurrentUser();
  const [storedPreference, setStoredPreference] = useTaskTogglePreference();

  // Get myTasks value from URL params
  const myTasksParam = searchParams.get('myTasks');

  // Determine myTasks value based on URL param or stored preference
  const myTasks =
    myTasksParam === 'true'
      ? true
      : myTasksParam === 'false'
      ? false
      : storedPreference !== null
      ? storedPreference
      : false;

  // Update stored preference when myTasks changes
  useEffect(() => {
    if (myTasksParam === 'true' || myTasksParam === 'false') {
      setStoredPreference(myTasksParam === 'true');
    }
  }, [myTasksParam, setStoredPreference]);

  const filters: TaskSearchFilters = {
    search: searchParams.get('search') || '',
    status: searchParams.has('status')
      ? (searchParams.get('status') as TaskSearchStatus | 'ALL')
      : 'ALL',
    sentBy: searchParams.get('sentBy') || 'all',
    myTasks,
    type: (searchParams.get('type') as TaskSearchType) || TaskSearchType.ALL,
    dueAtStart: searchParams.get('dueAtStart') || undefined,
    dueAtEnd: searchParams.get('dueAtEnd') || undefined,
    limit: parseInt(searchParams.get('tasks') || '20'),
    sortBy: searchParams.get('sortBy') as SearchTasksSortBy | undefined,
    sortDirection: searchParams.get('sortDirection') as
      | ModelSortDirection
      | undefined,
  };

  if (filters.myTasks) {
    filters.createdBy = id;
  }

  const setFilters = (newFilters: Partial<typeof filters>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        updatedParams.delete(key);
      } else {
        updatedParams.set(key, value.toString());
      }
    });

    setSearchParams(updatedParams);
  };

  return { filters, setFilters };
}
